import React, { useCallback, useEffect, useState } from "react";

import styles from "./Popup.module.scss";

type PopupProps = {
  children: React.ReactNode;
  onClose: () => void;
  isOpen?: boolean;
};

const Popup: React.FC<PopupProps> = ({ children, onClose, isOpen = true }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to ensure the component is mounted before animating in
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  const handleClose = useCallback(() => {
    setIsVisible(false);
    // Wait for animation to complete before calling onClose
    setTimeout(() => onClose(), 200);
  }, [onClose]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isVisible) {
        handleClose();
      }
    };

    if (isVisible) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [handleClose, isVisible]);

  if (!shouldRender) return null;

  const overlayClasses = `${styles.overlay} ${
    isVisible ? styles.visible : styles.closed
  }`;
  const popupClasses = `${styles.popup} ${
    isVisible ? styles.visible : styles.closed
  }`;

  return (
    <div className={overlayClasses} onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }} role="presentation" data-slot="popup-overlay">
      <div className={popupClasses} role="dialog" aria-modal="true">
        {children}
      </div>
    </div>
  );
};

export default Popup;
